import { query } from "@solidjs/router";

import { getServerEnv } from "~/_server/server";
import { getUserByEmail, updateDB } from "~/_server";
import { getXeroClient } from "~/_server/xero";
import {
	Invoice,
	Invoice as InvoiceType,
	Contact as ContactType,
	Payment,
	OnlineInvoice,
} from "xero-node";

// Shaped like xero-node's Address, but declared locally on purpose. Every other
// export here is "use server" and so becomes an RPC stub in the browser bundle;
// billingAddress is a plain helper and is kept, so any xero-node *value* it
// touched (Address.AddressTypeEnum) pulled the whole SDK client-side - and with
// it openid-client, whose shake256 blows up on "process is not defined".
type BillingAddress = {
	addressType: "POBOX";
	addressLine1: string;
	addressLine2: string;
	addressLine3: string;
	city: string;
	region: string;
	postalCode: string;
	country: string;
};

type InvoiceDTO = {
	invoiceId?: string;
	invoiceNumber?: string;
	date?: string | null;
	dueDate?: string | null;
	status?: Invoice["status"];
	total?: number | null;
	amountDue?: number | null;
	amountPaid?: number | null;
	contact?: { contactID?: string; name?: string; emailAddress?: string };
};

const toDTO = (i: InvoiceType): InvoiceDTO => ({
	invoiceId: i.invoiceID,
	invoiceNumber: i.invoiceNumber,
	date: i.date ? new Date(i.date).toISOString() : null,
	dueDate: i.dueDate ? new Date(i.dueDate).toISOString() : null,
	status: i.status,
	total: i.total ?? null,
	amountDue: i.amountDue ?? null,
	amountPaid: i.amountPaid ?? null,
	contact: i.contact
		? {
				contactID: i.contact.contactID,
				name: i.contact.name,
				emailAddress: i.contact.emailAddress,
			}
		: undefined,
});

const toMs = (d: unknown): number =>
	d ? new Date(d as any).getTime() : -Infinity;

// xero-node rejects with a plain object rather than an Error, so `.message` is
// undefined and the real reason (e.g. a duplicate invoice number) was lost.
const describeXeroError = (e: any): string => {
	const detail = e?.response?.body ?? e?.body ?? e?.message ?? e;

	try {
		return typeof detail === "string" ? detail : JSON.stringify(detail);
	} catch {
		return String(detail);
	}
};

const reverseDate = (d: Date): string =>
	`${d.getFullYear().toString().slice(2)}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;

async function withXeroRetry<T>(
	apiCall: () => Promise<T>,
	maxAttempts = 5,
): Promise<T> {
	let attempts = 0;

	while (attempts < maxAttempts) {
		try {
			return await apiCall();
		} catch (e: any) {
			attempts++;

			const isRateLimit = e.response?.statusCode === 429;
			const isConcurrent =
				e.response?.headers?.["x-rate-limit-problem"] === "concurrent";

			if ((isRateLimit || isConcurrent) && attempts < maxAttempts) {
				// Use Xero's retry-after header if it exists, otherwise exponential backoff
				const retryHeader = parseInt(e.response?.headers?.["retry-after"]);
				const waitTimeSeconds = retryHeader || Math.pow(2, attempts);

				// Convert to ms and add a bit of random jitter (0-200ms)
				const delay = waitTimeSeconds * 1000 + Math.random() * 200;

				console.warn(
					`Xero Rate Limit (${isConcurrent ? "Concurrent" : "Standard"}). Retrying in ${Math.round(delay)}ms... (Attempt ${attempts}/${maxAttempts})`,
				);

				await new Promise(resolve => setTimeout(resolve, delay));
			} else {
				// Log and re-throw the error so the calling function knows it failed
				console.error("Xero API Final Failure:", describeXeroError(e));
				throw e;
			}
		}
	}
	throw new Error("Max retry attempts reached");
}

export const createInvoice = async (
	userEmail: string,
	userName: string,
	lineItems: InvoiceType["lineItems"],
	itemId: number,
	date: string = "",
	// Distinguishes follow-on certificates (stages/amendments) that share the
	// parent's itemId - Xero invoice numbers must be unique.
	suffix: string = "",
): Promise<{ invoiceId: string | undefined; invoiceNumber: string | undefined}> => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		const contact = await getContactByEmail(userEmail, true);

		if (contact?.contactID) {
			let dateObj;
			if (date == "") {
				dateObj = new Date();
			} else {
				dateObj = new Date(date);
			}

			const invoiceNumber =  `RR-${reverseDate(dateObj)}-${String(itemId).padStart(5, "0")}${suffix}`;

			const invoice = {
				invoiceNumber,
				type: Invoice.TypeEnum.ACCREC,
				contact: { contactID: contact?.contactID ?? null, name: userName },
				lineItems,
				date: dateObj.toISOString(),
				dueDate: dateObj.toISOString(),
				status: Invoice.StatusEnum.AUTHORISED,
			};

			const invoiceId = await withXeroRetry(async () => {
				const result = await xeroClient.accountingApi.createInvoices(tenantId, {
					invoices: [invoice],
				});
				return result.body?.invoices?.[0]?.invoiceID ?? undefined;
			} );
			

			return {invoiceNumber, invoiceId};
		}
	} catch (e: unknown) {
		console.log("Error creating invoice:", describeXeroError(e));
	}

	return {invoiceNumber:undefined, invoiceId:undefined};
};

export const setInvoicePending = async (userId: number, invoiceId: string) => {
	"use server";

	try {
		await updateDB(`USER#${userId}`, `INVOICE#${invoiceId}`, {
			status: "pending",
			_ttl: 60 * 60 * 24 * 30,
		}); // pending for 30 days

		return { success: true };
	} catch (e: unknown) {
		console.error("Error setting invoice pending:", (e as Error).message);

		return { success: false, error: (e as Error).message };
	}
};

export const markInvoiceAsPaid = async (
	invoiceID: string,
	amount: number,
	// The Xero account the payment is booked to. It defaults to the Stripe account,
	// which is right for a card payment; a bank transfer has to name the bank
	// account it arrived in, or Xero shows it as paid through Stripe.
	options: { accountID?: string; reference?: string } = {},
) => {
	"use server";
	console.log( "Marking invoice as paid", invoiceID, amount, options.accountID ? "(account given)" : "(Stripe account)" );
	try {
		const { xeroClient, tenantId } = await getXeroClient();
		const accountID = options.accountID ?? getServerEnv().xeroAccountId;

		const payment = {
			invoice: { invoiceID },
			account: { accountID },
			date: new Date().toISOString().split("T")[0],
			amount,
			...( options.reference ? { reference: options.reference } : {} ),
		} as Payment;

		const result = await xeroClient.accountingApi.createPayment(
			tenantId,
			payment,
		);

		return JSON.parse( JSON.stringify( result.body.payments?.[0] ) ); 
	} catch (error) {
		console.error("Error markInvoiceAsPaid:", error);
	}

	return null;
};

// We store the Xero GUID on our records and never the RR- number, so anything
// starting from one of our own records has to look the invoice up this way
// rather than through getInvoiceByNumber.
export const getInvoiceById = async (
	invoiceId: string,
): Promise<InvoiceDTO | { error: string }> => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		return await withXeroRetry(async () => {
			const res = await xeroClient.accountingApi.getInvoice(tenantId, invoiceId);

			const invoice = res.body.invoices?.[0];

			return invoice ? toDTO(invoice) : { error: `No invoice found in Xero for ${invoiceId}` };
		});
	} catch (e: unknown) {
		console.error("Error fetching invoice by id:", describeXeroError(e));

		return { error: describeXeroError(e) };
	}
};

// Voiding leaves the invoice on the ledger marked VOIDED, which is what the
// accountant wants for an application that was never taken up - deleting would
// break the RR- number sequence. Xero refuses once a payment or credit note is
// attached, so the caller has to cope with a failure rather than assume one.
export const voidInvoice = async (invoiceId: string) => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		return await withXeroRetry(async () => {
			const res = await xeroClient.accountingApi.updateInvoice(tenantId, invoiceId, {
				invoices: [{ invoiceID: invoiceId, status: Invoice.StatusEnum.VOIDED }],
			});

			const status = res.body.invoices?.[0]?.status;

			return status === Invoice.StatusEnum.VOIDED
				? { success: true }
				: { success: false, error: `Xero left the invoice as ${status}` };
		});
	} catch (e: unknown) {
		console.error("Error voiding invoice:", describeXeroError(e));

		return { success: false, error: describeXeroError(e) };
	}
};

// Addresses arrive under two naming conventions - the WordPress import
// (address-1/town) and the registration form (addr1/city) - and plenty were
// typed with their own trailing comma.
const addressValue = (address: any, ...keys: string[]): string => {
	for (const key of keys) {
		const value = address?.[key];

		if (typeof value === "string" && value.trim()) {
			return value.trim().replace(/,+$/, "").trim();
		}
	}

	return "";
};

// Xero prints the POBOX address on an invoice; STREET is the delivery address.
// There is no separate company field - Name is the uniqueness key and drives
// invoice display - so the company goes on the first address line.
export const billingAddress = (address: any): BillingAddress | null => {
	const lines = [
		addressValue(address, "company"),
		addressValue(address, "address-1", "addr1"),
		addressValue(address, "address-2", "addr2"),
	].filter(Boolean);

	const city = addressValue(address, "town", "city");
	const postalCode = addressValue(address, "postcode");

	if (!lines.length && !city && !postalCode) return null;

	return {
		addressType: "POBOX",
		addressLine1: lines[0] ?? "",
		addressLine2: lines[1] ?? "",
		addressLine3: lines[2] ?? "",
		city,
		region: addressValue(address, "county"),
		postalCode,
		country: "United Kingdom",
	};
};

export const createContact = async (userEmail: string) => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		const user = await getUserByEmail(userEmail);

		if (user) {
			const contact: ContactType = {
				name: user.name,
				emailAddress: user.email,
				firstName: user.firstName,
				lastName: user.lastName,
			};

			// Without this the contact starts blank and every invoice raised
			// against it prints with no address.
			const address = billingAddress(user.address);

			if (address) contact.addresses = [address as any]; // addressType is an SDK enum; see BillingAddress

			const phone = addressValue(user.address, "telephone");

			if (phone) {
				contact.phones = [{ phoneType: "DEFAULT" as any, phoneNumber: phone }];
			}

			return await withXeroRetry(async () => {
				const result = await xeroClient.accountingApi.createContacts(tenantId, {
					contacts: [contact],
				});

				return result.body.contacts?.[0];
			});
		}
	} catch (error) {
		console.error("Error creating contact:", error);
	}

	return null;
};

// createContact only sets an address on contacts it creates, so anyone whose
// Xero contact predates that keeps a blank one - which is how Colin Young ended
// up with an invoice carrying no address. Fill it the next time we touch the
// contact rather than waiting for another backfill.
//
// Only ever fills a gap: a contact that already has a billing address is left
// alone, and a failure here must not stop an invoice being raised.
const topUpContactAddress = async (contact: ContactType, email: string) => {
	"use server";

	try {
		const existing = contact.addresses ?? [];
		const populated = (a: any) => a.addressLine1 || a.city || a.postalCode;

		if (existing.some(a => String(a.addressType) === "POBOX" && populated(a))) return;

		const user = await getUserByEmail(email);
		const address = billingAddress(user?.address);

		if (!address) return;

		const { xeroClient, tenantId } = await getXeroClient();

		// Sending addresses replaces the whole collection, so keep any populated
		// delivery address rather than blanking it.
		const keep = existing.filter(a => String(a.addressType) !== "POBOX" && populated(a));

		await withXeroRetry(async () =>
			xeroClient.accountingApi.updateContact(tenantId, contact.contactID as string, {
				contacts: [{ contactID: contact.contactID, addresses: [address as any, ...keep] }],
			}),
		);

		console.log("Filled in the missing Xero address for", email);
	} catch (err) {
		console.error("Could not top up the Xero address for", email, describeXeroError(err));
	}
};

export const getContactByEmail = async (
	email: string,
	createIfNotExists = false,
) => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		let contact = await withXeroRetry(async () => {
			const contactRes = await xeroClient.accountingApi.getContacts(
				tenantId,
				undefined,
				`EmailAddress=="${email}"`,
			);

			return contactRes?.body?.contacts?.[0];
		});

		if (contact?.contactID) {
			await topUpContactAddress(contact, email);
		} else if (createIfNotExists) {
			contact = (await withXeroRetry(async () => await createContact(email))) ?? undefined;
		}

		if (contact?.contactID) return contact;
	} catch (error) {
		console.error("Error fetching contact by email:", error);
	}
};

export const getInvoiceUrl = async (
	invoiceId: string,
): Promise<OnlineInvoice | { error: string }> => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		return await withXeroRetry(async () => {
			const invoice = await xeroClient.accountingApi.getOnlineInvoice(
				tenantId,
				invoiceId,
			);

			if (
				invoice.body.onlineInvoices &&
				invoice.body.onlineInvoices.length > 0
			) {
				return invoice.body.onlineInvoices[0];
			} else {
				return { error: "Invoice not found" };
			}
		});
	} catch (e: unknown) {
		console.error(e);
		return { error: (e as Error).message };
	}
};

export const getCustomerInvoices = query(async (email: string) => {
	"use server";
	try {
		const { xeroClient, tenantId } = await getXeroClient();

		const contact = await getContactByEmail(email, false);

		if (contact) {
			// Step 2: Get invoices for this contact
			const invoiceRes = await xeroClient.accountingApi.getInvoices(
				tenantId,
				undefined,
				`Contact.ContactID==Guid("${contact.contactID}")`,
				undefined, // order
				undefined, // IDs
				undefined, // invoiceNumbers
				undefined, // contact IDs
				["AUTHORISED", "PAID"], // statuses
				undefined, // page
				undefined, // includeArchived
				false, // whereOnlyInvoices
			);

			const invoices = (invoiceRes.body.invoices ?? [])
				.slice()
				.filter(inv => inv.status != Invoice.StatusEnum.DELETED)
				.sort((a, b) => toMs(b.date) - toMs(a.date)) // date DESC, nulls last
				.map(toDTO);

			return invoices;
		} else {
			return [];
		}
	} catch (e: unknown) {
		console.error(e);
		return [{ error: (e as Error).message }];
	}
}, "getCustomerInvoices");

// Bank transfers arrive quoting the human-readable invoice number (RR-260903-33803)
// rather than the Xero GUID, and we only ever store the GUID - so a manual payment
// has to be matched through Xero before it can be matched to one of our records.
export const getInvoiceByNumber = async (
	invoiceNumber: string,
): Promise<(InvoiceDTO & { lineDescriptions: string[] }) | { error: string }> => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		return await withXeroRetry(async () => {
			const res = await xeroClient.accountingApi.getInvoices(
				tenantId,
				undefined, // ifModifiedSince
				undefined, // where
				undefined, // order
				undefined, // IDs
				[invoiceNumber], // invoiceNumbers
			);

			const invoice = (res.body.invoices ?? []).find(
				i => i.status !== Invoice.StatusEnum.DELETED,
			);

			if (!invoice) {
				return { error: `No invoice found in Xero for ${invoiceNumber}` };
			}

			return {
				...toDTO(invoice),
				lineDescriptions: (invoice.lineItems ?? [])
					.map(l => l.description ?? "")
					.filter(Boolean),
			};
		});
	} catch (e: unknown) {
		console.error("Error fetching invoice by number:", describeXeroError(e));

		return { error: describeXeroError(e) };
	}
};
